import React from "react";
import usePrice from "hooks/usePrice";
import { AssetType } from "static/types";
import { ExchangeStatus } from "hooks/useExchangeTimeFrame";

import "./style.scss";

// ─── Strike filter config ────────────────────────────────────────────────────
// Set enabled=false to show all strikes (disables RT subscription limiting).
// range=0.30 means only strikes within ±30% of the median strike are shown.
export const STRIKE_FILTER = {
  enabled: true,
  range: 0.40,
};

export const filterStrikes = (strikes: number[]): number[] => {
  if (!STRIKE_FILTER.enabled || strikes.length === 0) return strikes;
  const mid = strikes[Math.floor(strikes.length / 2)];
  return strikes.filter(
    (s) => s >= mid * (1 - STRIKE_FILTER.range) && s <= mid * (1 + STRIKE_FILTER.range)
  );
};

export const buildOptionStrikeMap = <
  T extends { strike: number; expiration: string },
>(
  calls: T[],
  puts: T[],
  selectedExp: string,
): Map<number, { call?: T; put?: T }> => {
  const map = new Map<number, { call?: T; put?: T }>();
  const matchesExp = (c: T) => !selectedExp || c.expiration === selectedExp;

  for (const c of calls.filter(matchesExp)) {
    if (!map.has(c.strike)) map.set(c.strike, {});
    map.get(c.strike)!.call = c;
  }
  for (const c of puts.filter(matchesExp)) {
    if (!map.has(c.strike)) map.set(c.strike, {});
    map.get(c.strike)!.put = c;
  }
  return map;
};

export interface IOptionContract {
  id: number;
  strike: number;
  expiration: string;
  option_type: number;
  bid: number | null;
  ask: number | null;
  price: number | null;
  open_interest?: number | null;
  decimal_places?: number;
}

interface Props<T extends IOptionContract = IOptionContract> {
  call?: T;
  put?: T;
  strike: number;
  parentSymbol: string;
  exchange?: string;
  exchangeStatus?: ExchangeStatus;
  showOI?: boolean;
  activeClass?: string;
  strikeClass?: string;
  onDeal: (operation: "buy" | "sell", contract: T, price: number) => void;
}

const OptionChainRow = <T extends IOptionContract>({
  call,
  put,
  strike,
  parentSymbol,
  exchange,
  exchangeStatus,
  showOI,
  activeClass = "has-price clickable",
  strikeClass = "strike-col",
  onDeal,
}: Props<T>) => {
  const dp = call?.decimal_places ?? put?.decimal_places ?? 2;
  const expiration = call?.expiration ?? put?.expiration ?? "";

  const callPrices = usePrice({
    exchange,
    exchangeStatus,
    symbol: parentSymbol,
    assetType: AssetType.Option,
    ask: call?.ask,
    bid: call?.bid,
    price: call?.price,
    change: null,
    parentSymbol,
    strike,
    expiration,
    optionType: 1,
  });

  const putPrices = usePrice({
    exchange,
    exchangeStatus,
    symbol: parentSymbol,
    assetType: AssetType.Option,
    ask: put?.ask,
    bid: put?.bid,
    price: put?.price,
    change: null,
    parentSymbol,
    strike,
    expiration,
    optionType: 2,
  });

  const callBid = callPrices.bid ?? call?.bid ?? null;
  const callAsk = callPrices.ask ?? call?.ask ?? null;
  const putBid = putPrices.bid ?? put?.bid ?? null;
  const putAsk = putPrices.ask ?? put?.ask ?? null;

  const quotePrice = (v: number | null | undefined) => v ?? 0;
  const fmt = (v: number | null) =>
    v != null && v !== 0 ? v.toFixed(dp) : "—";

  const renderQuoteCell = (
    contract: T | undefined,
    price: number | null,
    operation: "buy" | "sell",
    optionType: 1 | 2,
  ) => {
    const dealContract =
      contract ??
      ({
        id: 0,
        strike,
        expiration,
        option_type: optionType,
        bid: null,
        ask: null,
        price: null,
      } as T);

    const hasContract = contract && contract.id;

    return (
      <td className={activeClass}>
        <button
          type="button"
          className="option-chain-cell-btn"
          disabled={!hasContract}
          onClick={() => hasContract && onDeal(operation, dealContract, quotePrice(price))}
        >
          {fmt(price)}
        </button>
      </td>
    );
  };

  return (
    <tr>
      {renderQuoteCell(call, callBid, "sell", 1)}
      {renderQuoteCell(call, callAsk, "buy", 1)}
      {showOI && <td>{call?.open_interest ?? "—"}</td>}
      <td className={strikeClass}>{strike}</td>
      {renderQuoteCell(put, putBid, "sell", 2)}
      {renderQuoteCell(put, putAsk, "buy", 2)}
      {showOI && <td>{put?.open_interest ?? "—"}</td>}
    </tr>
  );
};

export default OptionChainRow;
